import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { adminApi } from './api'

export type AdminUser = { id: number; email: string; name: string; role: string }

type AuthApi = {
  user: AdminUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<string | null>
  quickLogin: (email: string) => Promise<string | null>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthApi | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const { res, data } = await adminApi<{ user?: AdminUser }>('/api/admin/me')
    if (res.ok && data.user) setUser(data.user)
    else setUser(null)
  }, [])

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const login = useCallback(async (email: string, password: string) => {
    const { res, data } = await adminApi<{ success?: boolean; message?: string; user?: AdminUser }>(
      '/api/admin/login',
      { method: 'POST', json: { email, password } }
    )
    if (!res.ok || !data.user) return data.message || 'Login failed'
    setUser(data.user)
    return null
  }, [])

  const quickLogin = useCallback(async (email: string) => {
    const { res, data } = await adminApi<{ success?: boolean; message?: string; user?: AdminUser }>(
      '/api/admin/dev/quick-login/login',
      { method: 'POST', json: { email } }
    )
    if (!res.ok || !data.user) return data.message || 'Quick login failed'
    setUser(data.user)
    return null
  }, [])

  const logout = useCallback(async () => {
    await adminApi('/api/admin/logout', { method: 'POST' })
    setUser(null)
  }, [])

  const value = useMemo(
    () => ({ user, loading, login, quickLogin, logout, refresh }),
    [user, loading, login, quickLogin, logout, refresh]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAdminAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAdminAuth outside provider')
  return ctx
}
